import { RouterSelectors } from '@next/core-lib/router-store';
import { createSelector, createFeatureSelector } from '@ngrx/store';

import { CycleModuleState } from '../../models/cycle.model';
import { adapter } from '../reducers/cycle.reducer';

const { selectAll } = adapter.getSelectors();

export const getCycleModuleState = createFeatureSelector<CycleModuleState>('cycleModule');

export const getCyclesState = createSelector(getCycleModuleState, (state) => state.cycles);

export const getSelectedCycleId = createSelector(
  getCycleModuleState,
  RouterSelectors.getRouteParam('cycleId'),
  (state, cycleId) => {
    const routeCycleId = parseInt(cycleId, 10);
    if (!isNaN(routeCycleId) && routeCycleId > 0) {
      return routeCycleId;
    } else {
      return state.selectedCycleId;
    }
  },
);

export const getCycles = createSelector(getCyclesState, selectAll);

export const getSelectedCycle = createSelector(getSelectedCycleId, getCycles, (cycleId, cycles) =>
  cycles.find(({ id }) => id === cycleId),
);

export const getNextCycleNumber = createSelector(getCycles, (cycles) =>
  !!cycles && cycles.length > 0 ? Math.max(...cycles.map((c) => c.cycleNumber)) + 1 : 1,
);

export const getPreviousCycle = createSelector(
  getCycles,
  (cycles) =>
    !!cycles &&
    cycles.length > 0 &&
    cycles.reduce((prev, curr) => (prev.cycleNumber > curr.cycleNumber ? prev : curr)),
);

export const isCycleNumberAllowed = createSelector(
  getCycles,
  getSelectedCycle,
  (cycles, cycle) => (cycleNumber: number) =>
    (cycle && cycle.cycleNumber === cycleNumber && cycle.id) ||
    cycles.findIndex((c) => c.cycleNumber === cycleNumber) === -1,
);

export const getPropagationCompanies = createSelector(getCycleModuleState, (state) =>
  !!state && !!state.propagationCompanies
    ? [...state.propagationCompanies].sort((a, b) => a.name.localeCompare(b.name))
    : [],
);

export const getSelectedOrderId = createSelector(
  getCycleModuleState,
  (state) => state.selectedOrderId,
);

export const getSelectedOrder = createSelector(
  getSelectedCycle,
  getSelectedOrderId,
  (cycle, orderId) => cycle.orders.find((o) => o.id === orderId),
);

export const getSelectedSectionId = createSelector(
  getCycleModuleState,
  (state) => state.selectedSectionId,
);

export const getSelectedSection = createSelector(
  getSelectedCycle,
  getSelectedSectionId,
  (cycle, sectionId) => cycle.sections.find((o) => o.id === sectionId),
);

export const getSelectedMovementId = createSelector(
  getCycleModuleState,
  (state) => state.selectedMovementId,
);

export const getSelectedMovement = createSelector(
  getSelectedCycle,
  getSelectedMovementId,
  (cycle, movementId) => cycle.movements.find((o) => o.id === movementId),
);

export const getSections = createSelector(getSelectedCycle, (cycle) =>
  !!cycle && !!cycle.sections
    ? [...cycle.sections].sort((a, b) => a.name.localeCompare(b.name))
    : [],
);

export const getArrivals = createSelector(getSelectedCycle, (cycle) =>
  !!cycle && !!cycle.arrivals ? [...cycle.arrivals] : [],
);

export const getMovements = createSelector(getSelectedCycle, (cycle) =>
  !!cycle && !!cycle.movements ? [...cycle.movements] : [],
);

export const getHatcheries = createSelector(getCycleModuleState, (state) =>
  !!state && !!state.hatcheries
    ? [...state.hatcheries].sort((a, b) => a.name.localeCompare(b.name))
    : [],
);

export const getSelectedArrivalId = createSelector(
  getCycleModuleState,
  (state) => state.selectedArrivalId,
);

export const getSelectedArrival = createSelector(
  getSelectedCycle,
  getSelectedArrivalId,
  (cycle, arrivalId) => cycle.arrivals.find((o) => o.id === arrivalId),
);

export const getSelectedDepartureId = createSelector(
  getCycleModuleState,
  (state) => state.selectedDepartureId,
);

export const getSelectedDeparture = createSelector(
  getSelectedCycle,
  getSelectedDepartureId,
  (cycle, departureId) => cycle.departures.find((o) => o.id === departureId),
);
